// @ts-check
import Vue from 'vue';
import { getItemHashKey, saveState } from './helpers';
import { state } from '../state';
import { getComboStake } from './stake';
import { resetFolds } from './folds';
import { calculateBonuses, calculateSingleBonus } from './calculations';
import { configStore } from './config.store';
import { BETSLIP_TYPES, getSelectedTab, ITEM_TYPES } from 'bto-models/betslip';
import { setToastNotification } from '@bto-services/toast-notification';
import { getToastAdditionalTime, TOAST_NOTIFICATION_MESSAGE_STATUS } from 'bto-models/toast-notification';
import { addItem, removeItem, toggleItem, validateItem } from './items';
import AlgoOddRequest from '@bto-services/api/shared/algo-odd.request';
import { showBetslipValidation } from '@bto-services/modal';

/**
 * Get algo price
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 * @returns {Promise<{price:number, error:string}>}
 */
async function getAlgoPrice(item) {
  const match = state.matchItems[item.matchId];
  if (!match) {
    return Promise.reject('match already removed');
  }
  const algoOddRequest = new AlgoOddRequest({
    mid: item.matchId,
    sid: match.sportId,
    il: item.itemType === ITEM_TYPES.LIVE,
    tid: match.tournamentId,
    ht: match.homeTeam,
    at: match.awayTeam,
    tn: match.tournamentName,
    i: match.betRadarId,
    outi: state.algoItems[item.matchId].singles.map(it => ({
      oid: it.marketId,
      otoid: it.outcomeId,
      odd: +it.odd,
      issbv: !!it.sbv,
      // TODO: check if we need to send true also for new player markets
      ipm: it.itemType === ITEM_TYPES.PLAYER,
      sbv: it.sbv || '',
      id: it.identifier || null,
    })),
  });

  return algoOddRequest.execute().then(resp => {
    const itemKey = getItemHashKey(item);
    if (!state.algoItems[item.matchId] || (!item.algo && !state.algoItems[item.matchId].singles.some(single => getItemHashKey(single) === itemKey))) {
      return Promise.reject('algo item not found');
    }
    if (!resp.data || !resp.data.TotalOdd) {
      return {
        price: 0,
        error: resp.data && resp.data.ResponseErrorMsg
      };
    }
    return {
      price: resp.data.TotalOdd,
      error: resp.data && resp.data.ResponseErrorMsg
    };
  }).catch(() => { 
    return {
      price: 0,
      error: Vue.prototype.$translate.text('BS.IncompatibleAlgoItem')
    };
  });
}

/**
 * Change algo item odd
 * @param {import('bto-models/betslip/interfaces').BetslipItemKey} item - betslip item
 * @param {string|number} newOdd - new odd for item
 */
function changeAlgoItemOdd(item, newOdd) {
  const algoItem = state.algoItems[item.matchId].algoItem;
  const itemKey = getItemHashKey(algoItem);
  const oddDiff = (+algoItem.odd) - (+newOdd);
  const upDown = oddDiff < 0 ? 1 : oddDiff === 0 ? 0 : -1;
  Vue.set(state.algoItems[item.matchId].algoItem, 'odd', newOdd);
  if (state.algoItems[item.matchId].isActive) {
    Vue.set(state.items[itemKey], 'upDown', upDown);
    Vue.set(state.items[itemKey], 'odd', newOdd);
    calculateSingleBonus(itemKey);
    calculateBonuses();
  }
  saveState();
}

function getAlgoActiveDefault() {
  if (!APP_SETTINGS.betslip_v2.algoTabMode) {
    return true;
  }
  if (Object.keys(state.items).length < 2) {
    return true;
  }
  return getSelectedTab(state) === BETSLIP_TYPES.COMBI;
}

/**
 * Add algo item to betslip
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 * @param {import('bto-models/betslip/interfaces').BetslipMatchItem} match - betslip match item
 */
export function addAlgoItem(item, match) {
  // const isAlgoEnabled = (configStore.settings.ALGO_ENABLED || true) && !state.quickBetActive;
  // if (isAlgoEnabled && item.itemType === ITEM_TYPES.PRE_MATCH) {
  const error = validateItem(item);
  if(error) {
    showBetslipValidation(error);
    return;
  }
  const ALGO_MARKET_ID = configStore.settings.ALGO_MARKET_ID;
  const ALGO_OUTCOME_ID = configStore.settings.ALGO_OUTCOME_ID;
  if (!state.algoItems[item.matchId]) {
    Vue.set(state.algoItems, item.matchId, {
      singles: [],
      algoItem: {
        ...item,
        marketId: ALGO_MARKET_ID,
        outcomeId: ALGO_OUTCOME_ID,
        algo: true,
      },
      isActive: getAlgoActiveDefault(),
    });
  }

  const algoSingleItem = item;
  state.algoItems[item.matchId].singles.push(algoSingleItem);
  const singleIdx = state.algoItems[item.matchId].singles.length - 1;

  if (singleIdx === 0) {
    Vue.set(state.algoItems[item.matchId].singles[singleIdx], 'canCombine', true);
  }

  if (state.algoItems[item.matchId].singles.length > 1) {
    getAlgoPrice(item).then(resp => {
      if (resp.price) {
        changeAlgoItemOdd(item, resp.price);
        Vue.set(state.algoItems[item.matchId].singles[singleIdx], 'canCombine', true);
        return;
      }
      if (APP_SETTINGS.betslip_v2.keepIncompatibleItems === '2') {
        changeAlgoItemOdd(item, '1');
        return;
      }
      // If validation fails remove item from algo and add Item regularly
      const removeIncompatibleItem = !(+APP_SETTINGS.betslip_v2.keepIncompatibleItems) || APP_SETTINGS.betslip_v2.keepIncompatibleItems === '1';
      if (state.algoItems[item.matchId].isActive && removeIncompatibleItem) {
        // addItem(item, match);
        const toastKey = `${APP_SETTINGS.player.id}_algo_incompatible`;
        const expireTime = Date.now() + getToastAdditionalTime();
        setToastNotification(toastKey, {
          playerId: APP_SETTINGS.player.id,
          status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
          expireTime,
          //TODO: Send culture or require key to translate
          message: (resp.error && Vue.prototype.$translate.text(resp.error)) || Vue.prototype.$translate.text('BS.IncompatibleAlgoItem'),
        });
      }
      if (removeIncompatibleItem) {
        removeAlgoItem(item);
        // If settings is set to keep incompatible items in betslip add item to betslip
        if (APP_SETTINGS.betslip_v2.keepIncompatibleItems === '1') {
          addItem(item, match);
          // Deactivate algo if we are in algo tab mode and we have an incompatible item
          if (APP_SETTINGS.betslip_v2.algoTabMode && state.algoItems[item.matchId].isActive) {
            toggleAlgoActive(item);
          }
        }
      }
    });
  }



  if (!state.algoItems[item.matchId].isActive) {
    addItem(algoSingleItem, match);
    return;
  }
  const itemKey = getItemHashKey(state.algoItems[item.matchId].algoItem);
  if (!state.items[itemKey]) {
    addItem(state.algoItems[item.matchId].algoItem, match);
  }

}

/**
 * Remove algo item from betslip
 * @param {import('bto-models/betslip/interfaces').BetslipItemKey} item - betslip item
 */
export function removeAlgoItem(item) {
  const itemKey = getItemHashKey(item);
  const idx = state.algoItems[item.matchId].singles.findIndex(single => getItemHashKey(single) === itemKey);
  if (state.items[itemKey] && !state.algoItems[item.matchId].isActive) {
    removeItem(item);
  }
  if (idx === -1 || !state.algoItems[item.matchId]) {
    return;
  }

  Vue.delete(state.algoItems[item.matchId].singles, idx);

  if (state.algoItems[item.matchId].singles.length === 0 && state.algoItems[item.matchId].isActive) {
    let rmItem = state.algoItems[item.matchId].algoItem;
    if (state.items[getItemHashKey(rmItem)]) {
      removeItem(rmItem);
    }
    return;
  }
  if (state.algoItems[item.matchId].singles.length === 1) {
    state.algoItems[item.matchId].algoItem = {
      ...state.algoItems[item.matchId].singles[0],
      marketId: configStore.settings.ALGO_MARKET_ID,
      outcomeId: configStore.settings.ALGO_OUTCOME_ID,
      canCombine: true,
      algo: true,
    };
    const algoItemKey = getItemHashKey(state.algoItems[item.matchId].algoItem);
    if (state.items[algoItemKey]) {
      Vue.set(state.items, algoItemKey, state.algoItems[item.matchId].algoItem);
      calculateBonuses();
    }
    saveState();
    return;
  }
  const algoItem = state.algoItems[item.matchId].algoItem;
  getAlgoPrice(algoItem).then(resp => {
    if (resp.price) {
      changeAlgoItemOdd(item, resp.price);
      return;
    }
    changeAlgoItemOdd(item, '1');
  });
}

/**
 * Set first item to algo  
 * If we have a single item in the betslip that was not from algo remove it and add it to algo
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 * @param {import('bto-models/betslip/interfaces').BetslipMatchItem} match - betslip match item
 */
function setFirtItemToAlgo(item, match) {
  if (!state.algoItems[item.matchId] && state.matchItems[item.matchId]) {
    const regularItemKey = Object.keys(state.items).find(key => state.items[key].matchId === item.matchId);
    if (regularItemKey) {
      const regularItem = { ...state.items[regularItemKey] };
      removeItem(state.items[regularItemKey]);
      addAlgoItem(regularItem, match);
    }
  }
}

/**
 * Activate first item in algo  
 * If we have a single item in the betslip and it is not in algo mode swap it to algo
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 */
function activateFirstItemInAlgo(item) {
  if (state.algoItems[item.matchId]
    && !state.algoItems[item.matchId].isActive
    && state.algoItems[item.matchId].singles.length === 1) {
    toggleAlgoActive(item);
  }
}

/**
 * Toggle algo item from betslip
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 * @param {import('bto-models/betslip/interfaces').BetslipMatchItem} match - betslip match item
 */
export function toggleAlgoItem(item, match) {
  const isAlgoEnabled = configStore.settings.ALGO_ENABLED && !state.quickBetActive;
  const algoItemTypes = [ITEM_TYPES.PRE_MATCH, ITEM_TYPES.LIVE, ITEM_TYPES.PLAYER];
  if (isAlgoEnabled && algoItemTypes.some(type => item.itemType === type)) {
    const itemKey = getItemHashKey(item);
    const algoSingleItem = state.algoItems[item.matchId]
      && state.algoItems[item.matchId].singles.find(single => getItemHashKey(single) === itemKey);

    // If item is in algo remove it
    if (algoSingleItem) {
      return removeAlgoItem(item);
    }
    // If item is not in algo and is not in betslip try to add to algo
    if (!state.items[itemKey]) {
      // If we have a single item in the betslip that was not from algo remove it and add it to algo
      setFirtItemToAlgo(item, match);
      // If we have a single item in the betslip and it is not in algo mode swap it to algo
      activateFirstItemInAlgo(item);
      return addAlgoItem(item, match);
    }
  }
  return toggleItem(item, match);
}

/**
 * Toggle algo item active state
 * @param {import('bto-models/betslip/interfaces').BetslipItem} item - betslip item
 */
export function toggleAlgoActive(item) {
  state.algoItems[item.matchId].isActive = !state.algoItems[item.matchId].isActive;
  const match = state.matchItems[item.matchId];

  if (state.algoItems[item.matchId].isActive) {
    state.algoItems[item.matchId].singles.forEach(single => {
      removeItem(single);
    });
    addItem(state.algoItems[item.matchId].algoItem, match);
  } else {
    const algoMatch = state.algoItems[item.matchId];
    removeItem(state.algoItems[item.matchId].algoItem);
    algoMatch.singles.forEach(single => {
      addItem(single, match);
    });
    Vue.set(state.algoItems, item.matchId, algoMatch);
  }
  const lastComboStake = getComboStake();
  resetFolds(lastComboStake);
  calculateBonuses();
  saveState();
}